// Reglas de la tienda sin DOM: tallas, envío, totales locales y conversión del catálogo del API.
import type { Product, Shipping } from '../lib/types';

export interface Talla {
  t: string;
  espalda: [number, number];
  pecho: [number, number];
  cuello: [number, number];
  razas: string;
}

export const TALLAS: Talla[] = [
  { t: 'XXS', espalda: [18, 22], pecho: [28, 34], cuello: [18, 22], razas: 'Chihuahua, Toy poodle' },
  { t: 'XS', espalda: [23, 27], pecho: [34, 40], cuello: [22, 26], razas: 'Yorkshire, Pomerania, Pinscher' },
  { t: 'S', espalda: [28, 32], pecho: [40, 46], cuello: [26, 30], razas: 'Shih Tzu, Maltés, Dachshund mini' },
  { t: 'M', espalda: [33, 38], pecho: [46, 54], cuello: [30, 36], razas: 'Schnauzer mini, Bulldog francés, Beagle' },
  { t: 'L', espalda: [39, 45], pecho: [54, 64], cuello: [36, 42], razas: 'Cocker, Border collie, Shiba' },
  { t: 'XL', espalda: [46, 54], pecho: [64, 76], cuello: [42, 50], razas: 'Golden retriever, Labrador' },
];

export function recomendar(espaldaIn: number | string, pechoIn: number | string): { talla: string; nota: string } | null {
  const espalda = +espaldaIn, pecho = +pechoIn;
  if (!espalda) return null;
  let i = TALLAS.findIndex((s) => espalda <= s.espalda[1]);
  if (i === -1) i = TALLAS.length - 1;
  let nota = '';
  if (pecho && pecho > TALLAS[i]!.pecho[1]) {
    const j = TALLAS.findIndex((s) => pecho <= s.pecho[1]);
    i = j === -1 ? TALLAS.length - 1 : j;
    nota = 'Subimos una talla porque su pecho es amplio.';
  } else if (espalda >= TALLAS[i]!.espalda[1] - 1 && i < TALLAS.length - 1) {
    nota = 'Está al límite: si le gusta holgada, elige ' + TALLAS[i + 1]!.t + '.';
  }
  return { talla: TALLAS[i]!.t, nota };
}

/** CDMX: códigos postales 01000 a 16999. */
export function esCdmx(cp: string | null | undefined): boolean {
  const n = parseInt(String(cp ?? '').slice(0, 2), 10);
  return n >= 1 && n <= 16;
}

/** Producto con los nombres de campo que usa la interfaz (los del prototipo). Precio en pesos. */
export interface Prenda {
  id: string;
  nombre: string;
  cat: string;
  precio: number;
  color: string;
  color2: string;
  forma: string;
  patron: string;
  badge: string;
  rating: number;
  resenas: number;
  material: string;
  desc: string;
  activo: boolean;
  tallas: string[];
  stock: Record<string, number>;
  /** Fotos subidas desde la administración; vacío = se dibuja la ilustración. */
  fotos: string[];
  destacado: boolean;
}

const FORMAS = ['sueter', 'hoodie', 'impermeable', 'vestido', 'pijama', 'bandana', 'tote', 'scrunchie'];
const PATRONES = ['rayas', 'puntos', 'corazones', 'huellas', 'cuadros', 'liso'];
const HEX = /^#[0-9a-f]{6}$/i;
const ID = /^[a-z0-9-]{2,40}$/;
const TALLA = /^[\p{L}\d]{1,10}$/u;
// Solo fotos de nuestro almacenamiento (contenedor img), que además es lo único que permite la CSP
const FOTO = /^https:\/\/[a-z0-9]+\.blob\.core\.windows\.net\/img\/products\/[a-z0-9-]+\.(webp|jpg|png)$/;
// El API no maneja inventario todavía: cada talla publicada cuenta como disponible hasta el tope por línea del cobro.
export const MAX_POR_LINEA = 10;

/**
 * Convierte el catálogo del API al modelo de la interfaz. Lo que va a atributos o a SVG sin escapar
 * (id, colores, forma, patrón, tallas) se valida aquí; los textos se escapan al dibujar.
 */
export function desdeApi(products: Product[]): Prenda[] {
  return products
    .filter((p) => p && ID.test(p.id) && Number.isFinite(p.priceCents))
    .map((p) => {
      const colores = (p.colors ?? []).filter((c) => HEX.test(c));
      const tallas = (p.sizes ?? []).filter((t) => TALLA.test(t));
      return {
        id: p.id,
        nombre: String(p.name ?? ''),
        cat: String(p.category ?? ''),
        precio: p.priceCents / 100,
        color: colores[0] ?? '#FFD1E0',
        color2: colores[1] ?? colores[0] ?? '#FF8FB3',
        forma: FORMAS.includes(p.shape) ? p.shape : 'sueter',
        patron: PATRONES.includes(p.pattern) ? p.pattern : 'liso',
        badge: String(p.badge ?? ''),
        rating: Number(p.rating) || 0,
        resenas: Math.max(0, Math.trunc(Number(p.reviews) || 0)),
        material: String(p.material ?? ''),
        desc: String(p.description ?? ''),
        activo: p.active !== false,
        tallas: tallas.length ? tallas : ['Única'],
        // Con inventario: las piezas reales (tope por línea del cobro); sin inventario: el tope
        stock: Object.fromEntries(
          (tallas.length ? tallas : ['Única']).map((t) => [t, p.stock ? Math.max(0, Math.min(MAX_POR_LINEA, Math.trunc(Number(p.stock[t]) || 0))) : MAX_POR_LINEA]),
        ),
        fotos: (p.images ?? []).filter((u) => FOTO.test(u)),
        destacado: p.featured === true,
      };
    });
}

export interface Cupon {
  codigo: string;
  pct: number;
  activo: boolean;
  minimo?: number;
  vence?: string;
}

export interface LineaBolsa {
  id: string;
  talla: string;
  cant: number;
}

export const cuponVale = (c: Cupon | undefined, sub?: number, hoy = new Date().toISOString().slice(0, 10)) =>
  !!c && c.activo && (!c.vence || c.vence >= hoy) && (sub == null || !c.minimo || sub >= c.minimo);

/** Totales que se muestran mientras el API no responde (mismas reglas que el prototipo, en pesos). */
export function totalesLocales(bolsa: LineaBolsa[], prendas: Prenda[], envio: Shipping, cupones: Cupon[], codigo: string) {
  const items = bolsa.map((i) => ({ ...i, p: prendas.find((p) => p.id === i.id) })).filter((i): i is LineaBolsa & { p: Prenda } => !!i.p);
  const sub = items.reduce((a, i) => a + i.p.precio * i.cant, 0);
  const cup = cupones.find((c) => c.codigo === codigo && cuponVale(c, sub));
  const descCupon = cup ? Math.round((sub * cup.pct) / 100) : 0;
  const base = sub - descCupon;
  const gratisDesde = envio.freeFromCents / 100;
  const gratis = base >= gratisDesde;
  const costoEnvio = !items.length || gratis ? 0 : envio.standardCents / 100;
  return { items, sub, cup, descCupon, base, envio: costoEnvio, gratis, falta: Math.max(0, gratisDesde - base), total: base + costoEnvio };
}
